<template>
<div :style="editorRootStyles">
	<div :class="{ 'dx-card': true, 'ux-settings-previewer': true, 'doublewide': needsDoubleWidth }">
		<div class="title">
			<span>Preview</span>
			<span v-if="hasPreviewOptionChanges" class="dx-badge">Unsaved changes from preview below</span>
		</div>
		<ux-list :preview="true" :settings="settingsData" :context="props.context" v-if="props.type == 'list'" />
		<ux-datagrid :preview="true" :settings="settingsData" :context="props.context" @previewOptionsChanged="handlePreviewOptionsChanged" v-else-if="props.type == 'datagrid'" />
		<ux-calendar :preview="true" :settings="settingsData" :context="props.context" v-else-if="props.type == 'calendar'" />
		<ux-imageview :preview="true" :settings="settingsData" :context="props.context" v-else-if="props.type == 'imageview'" />
		<ux-html :preview="true" :settings="settingsData" :context="props.context" v-else-if="props.type == 'html'" @previewOptionsChanged="handlePreviewOptionsChanged" />
	</div>
	<div class="dx-card" style="padding:10px;flex-grow:2;max-width:500px;overflow-y:scroll;">
		<div v-if="subeditorData" class="ux-settings-subeditor-title">
			Editing {{ subeditorData.label || subeditorData.type }} tab
		</div>
		<UxSettingsEditorForm :type="props.type" :settings="settingsData" :context="props.context" @settingsChanged="handleSettingsChanged" @triggerSubeditor="startSubeditor" v-if="!subeditorData" />
		<UxSettingsEditorForm :type="subeditorData.type" :settings="subeditorData.subset" :context="props.context" @settingsChanged="handleSubsetChanged" v-else />
	</div>
</div>
<div class="ux-settings-buttons" style="justify-content: space-between;">
	<DxButton type="default" text="Save changes" @click="saveCellSettings" />
	<DxButton type="danger" text="Delete cell" @click="verifyDelete" />
</div>
</template>

<script setup>
/* eslint-disable */
import { defineProps, defineEmits, ref, watch, computed } from 'vue';
import DxButton from 'devextreme-vue/button';
import { confirm } from 'devextreme/ui/dialog';
import UxList from '../components/ux/ux-list';
import UxDatagrid from '../components/ux/ux-datagrid';
import UxCalendar from '../components/ux/ux-calendar';
import UxImageview from '../components/ux/ux-imageview';
import UxHtml from '../components/ux/ux-html';
import UxSettingsEditorForm from './ux-settings-editor-form.vue';

const props = defineProps({
	type: String,
	existingSettings: Object,
	recommendedWidth: Number,
	useRecommendedWidth: Boolean,
	context: Object,				// NB: Context is the parent page object -- but make sure we do NOT do any changes to it.
});
const emit = defineEmits(['saveSettings', 'deleteCell']);

const settingsData = ref({});					// All settings for the cell being edited, passed to the ux-* component.
const hasPreviewOptionChanges = ref(false);		// If our preview options in the component have changed, for display purposes.
const lastColumnOptions = ref(null);			// The recorded preview column options, for saving changes to width/visibility/etc in DataGrids.
const lastRichtextContents = ref(null);			// The recorded contents of the richtext editor, for saving changes to html in ux-html components.
const needsDoubleWidth = computed(() => {
	return props.type == 'datagrid' || props.type == 'mdt-subgrid' || props.type == 'calendar';
});

// When props settings change, update our settingsData with a clone. We don't want to directly edit the prop object -- since we can hit cancel,
// and go back to our previous/original settings stored in our parent component. The ux-setting-editor-form component then takes this
// and relays changes back to us, which we replace our settingsData with.
watch(props, () => {
	settingsData.value = props.existingSettings ? JSON.parse(JSON.stringify(props.existingSettings)) : {};
	hasPreviewOptionChanges.value = false;
	lastColumnOptions.value = null;
	lastRichtextContents.value = null;
	subeditorData.value = null;
});
const handleSettingsChanged = (newSettings) => {
	settingsData.value = newSettings;
}

// Handle the save button - we use a clone just in case, although it probably isn't necessary.
// We must also merge in our preview option changes - column changes for datagrids, richtext changes for html.
const saveCellSettings = () => {
	let clonedSettings = JSON.parse(JSON.stringify(settingsData.value));
	if (lastColumnOptions.value) {
		if (!clonedSettings.columns)
			clonedSettings.columns = {};
		for (let fieldKey in lastColumnOptions.value) {
			if (!clonedSettings.columns[fieldKey])
				clonedSettings.columns[fieldKey] = {};
			for (let optionKey in lastColumnOptions.value[fieldKey]) {
				clonedSettings.columns[fieldKey][optionKey] = lastColumnOptions.value[fieldKey][optionKey];
			}
		}
	} else if (lastRichtextContents.value) {
		// TODO: Sanitization check, on backend?
		clonedSettings.content = String(lastRichtextContents.value);
	} else {
		// If we don't have any records of any changes, try to use the existing value.
	}
	
	emit('saveSettings', clonedSettings);
}

// Handle the delete button, confirming the deletion first.
const verifyDelete = () => {
	confirm("Are you sure you want to delete this cell?", "Confirm deletion").then(result => {
		if (result)
			emit('deleteCell');
	});
}

// Handle the preview options changed event from the components like ux-datagrid: called when temporary changes are made (but not persisted yet) to:
// - our datagrid column options (column widths, visibility, etc)
// - our html editor contents
const handlePreviewOptionsChanged = (e) => {
	hasPreviewOptionChanges.value = true;
	if (props.type == 'datagrid') {
		lastColumnOptions.value = e;
	} else if (props.type == 'html') {
		lastRichtextContents.value = e;
	}
}

// Editor root styles for our layout - we sometimes will use widths recommended by our parent component for accurate preview of sizing.
const editorRootStyles = computed(() => {
	let calculatedWidth = props.useRecommendedWidth && props.recommendedWidth ? `${props.recommendedWidth + 500}px` : undefined;
	return {
		display: 'flex',
		height: 'calc(100% - 50px)',
		width: calculatedWidth,
	}
});

// Subeditor handling: the editor form component can ask for a subeditor, which swaps out the form with another one that has a narrow view for editing a specific section of the settingsData.
// Because we preserve the same settingsData object, all changes will be automatically kept.
const subeditorData = ref(null);
const startSubeditor = (e) => {
	console.log("Starting subeditor", e);
	subeditorData.value = e;
}
const handleSubsetChanged = (e) => {
	console.log("Subeditor changed", e);
}

</script>

<style lang="scss">
.ux-settings-buttons {
	height: 50px;
	display: flex;
    flex-direction: row-reverse;
    align-items: flex-end;
}
.ux-settings-previewer {
	margin-right: 20px;
	padding: 10px;
	min-width: 200px;
	width: 20vw;
	flex-grow: 1;
}
.ux-settings-previewer > .title {
	border-bottom: 1px dashed var(--dx-color-border);
	margin-bottom: 5px;
	padding: 5px;
	font-style: italic;
	font-weight: 500;
	display: flex;
	gap: 10px;
	align-items: center;
    justify-content: center;
	min-height: 32px;
}
.ux-settings-previewer.doublewide {
	width: 50vw;
	@media screen and (min-width: 1900px) {
		width: 100%;
	}
}
.ux-settings-subeditor-title {
	padding: 10px;
    margin-bottom: 15px;
    background-color: var(--dx-color-primary);
    font-size: 1.2em;
    font-weight: 500;
    margin-top: -10px;
    margin-left: -10px;
    margin-right: -10px;
    color: white;
}
</style>